import type { jsPDF } from 'jspdf';
import { keys } from 'ramda';

import type { FontItem, FontMap } from './create-pdf';
import type { AlineFontName } from '../../../slate-api';

/**
 * Load all fonts that appear in an Aline document for use in a PDF.
 */
export function loadPDFFonts(pdf: jsPDF, options: { fontMap: FontMap }): void {
  const { fontMap } = options;
  fontMap.keys().forEach(fontName => findOrCreatePDFFontFamily(pdf, { fontMap, fontName }));
}

/**
 * Checks if a font exists in the PDF already.  If not, loads the font family and adds it to the PDF.
 */
function findOrCreatePDFFontFamily(
  pdf: jsPDF,
  options: {
    fontMap: FontMap;
    fontName: AlineFontName;
  }
): AlineFontName {
  const { fontMap, fontName } = options;

  try {
    const fontList = pdf.getFontList();
    const pdfFont = fontList[fontName];
    const fontItem = fontMap.get(fontName);
    if (!pdfFont && fontItem) {
      keys(fontItem).forEach(fontStyle => {
        const b64Font = fontItem[fontStyle];
        const fileName = `${fontName}-${fontStyle}`;

        pdf.addFileToVFS(fileName, b64Font);
        pdf.addFont(
          fileName,
          fontName,
          getStyleFromFontItemKey(fontStyle),
          getWeightFromFontItemKey(fontStyle)
        );
      });

      return fontName;
    }

    return fontName;
  } catch (error) {
    console.error(`Error loading font ${fontName}:`, error);
    return fontName;
  }
}

function getStyleFromFontItemKey(key: keyof FontItem): 'normal' | 'italic' {
  switch (key) {
    case 'italic':
    case 'boldItalic':
      return 'italic';
    case 'bold':
    case 'regular':
      return 'normal';
  }
}

function getWeightFromFontItemKey(key: keyof FontItem): 'normal' | 'bold' {
  switch (key) {
    case 'bold':
    case 'boldItalic':
      return 'bold';
    case 'italic':
    case 'regular':
      return 'normal';
  }
}
